import {
  FieldOrientation,
  VerticalOrientation
} from './../activities/print-template-activity';
import {
  ConditionTarget,
  ConditionValue,
  OperatorTypes
} from './../../components/system/condition-builder/condition-builder.model';
import { Validators } from '@angular/forms';
import { fabric } from 'fabric';
import { DataEntityListItem } from '../../components/workflow/activities/print-template-activity/print-template-activity-editor/print-template-activity-editor.component';
import {
  TemplateField,
  TemplateFieldType,
  ActivityModel,
  Activity,
  Operators,
  FormActivity,
  FormActivityModel
} from '../activities';
import {
  ConditionOperator,
  defaultOperators
} from 'src/app/components/system/condition-builder/condition-builder.model';
import { Observable, of, map, forkJoin, tap } from 'rxjs';
import { ActivityNavigationInfo } from '../activity-navigation-Info';
import { ExternalDataSource } from '../external-data-source';
import { DataEntityFactory } from 'src/app/services';

export class DependentEntityValue {
  templateCode: string;
  value: string;
  dataEntityTypeCode: string;
}

export class DataEntity {
  dependentEntityValues: DependentEntityValue[] = [];

  public static evalCriteria(
    entities: DataEntity[],
    entitiesMap: { [key: string]: DataEntity },
    group?: any
  ): Observable<boolean> {
    if (group) {
      const conditionSubs: Observable<any>[] = [];

      group.conditions.forEach(c => {
        if (c.isGroup === true) {
          conditionSubs.push(this.evalCriteria(entities, entitiesMap, c.group));
        } else {
          let entity = entities.find(e => e.templateCode === c.field);

          // if not a entity on this activity, check the prepared dependent entities
          if (!entity) {
            entity = entitiesMap[c.field];
          }

          if (entity) {
            const sub = entity.getFormulaValue().pipe(
              map(val => {
                if (c.formulaOperator === OperatorTypes.IS.operator) {
                  // For multi-value lists (comma-separated), check if the criteria value is IN the list
                  if (typeof val === 'string' && val.indexOf(',') > -1) {
                    const values = val.split(',').map(v => v.trim());
                    c.result = values.includes(c.val);
                  } else {
                    c.result = val == c.val;
                  }
                } else if (
                  c.formulaOperator === OperatorTypes.IS_NOT.operator
                ) {
                  // For multi-value lists (comma-separated), check if the criteria value is NOT IN the list
                  if (typeof val === 'string' && val.indexOf(',') > -1) {
                    const values = val.split(',').map(v => v.trim());
                    c.result = !values.includes(c.val);
                  } else {
                    c.result = val != c.val;
                  }
                } else if (
                  c.formulaOperator === OperatorTypes.GREATER_THAN.operator
                ) {
                  c.result = parseFloat(val || '') > parseFloat(c.val || '');
                } else if (c.formulaOperator === '>=') {
                  // OperatorTypes.GREATER_THAN_OR_EQUALS.operator) {
                  c.result = parseFloat(val || '') >= parseFloat(c.val || '');
                } else if (
                  c.formulaOperator === OperatorTypes.LESS_THAN.operator
                ) {
                  c.result = parseFloat(val || '') < parseFloat(c.val || '');
                } else if (c.formulaOperator === '<=') {
                  // OperatorTypes.LESS_THAN_OR_EQUALS.operator) {
                  c.result = parseFloat(val || '') <= parseFloat(c.val || '');
                } else if (
                  c.formulaOperator === OperatorTypes.EXISTS.operator
                ) {
                  c.result =
                    val !== null &&
                    val !== undefined &&
                    val.toString().length > 0;
                } else if (
                  c.formulaOperator === OperatorTypes.NOT_EXISTS.operator
                ) {
                  c.result =
                    val === null ||
                    val === undefined ||
                    val.toString().length === 0;
                } else if (
                  c.formulaOperator === OperatorTypes.CONTAINS.operator
                ) {
                  if (val && c.val) {
                    c.result =
                      val
                        .toString()
                        .toLowerCase()
                        .indexOf(c.val.toString().toLowerCase()) > -1;
                  } else {
                    c.result = false;
                  }
                }
                return c.result;
              })
            );

            conditionSubs.push(sub);
          }
        }
      });

      // Wait for all condition subscriptions to complete before evaluating
      if (conditionSubs.length > 0) {
        return forkJoin(conditionSubs).pipe(
          map(() => {
            // now evaluate the group based on the logical operator
            if (group.logicalOperator === 0) {
              return group.conditions.every(c => c.result === true);
            } else {
              return group.conditions.some(c => c.result === true);
            }
          })
        );
      } else {
        // No async conditions, evaluate immediately
        // now evaluate the group based on the logical operator
        if (group.logicalOperator === 0) {
          return of(group.conditions.every(c => c.result === true));
        } else {
          return of(group.conditions.some(c => c.result === true));
        }
      }
    }

    return of(false);
  }

  public static prepareDependentEntities(activity: Activity<ActivityModel>) {
    // Prepare any dependent entities here
    if (
      !activity.preparedDependentEntitiesMap ||
      Object.keys(activity.preparedDependentEntitiesMap).length === 0
    ) {
      activity.preparedDependentEntitiesMap = {};

      if (
        activity &&
        activity instanceof FormActivity &&
        (<FormActivityModel>activity.model).formLayoutModel
          .dependentEntityValues
      )
        (<FormActivityModel>(
          activity.model
        )).formLayoutModel.dependentEntityValues.forEach(dependentEntity => {
          const entity = DataEntityFactory.createDataEntity(
            dependentEntity.dataEntityTypeCode
          );

          entity.templateCode = dependentEntity.templateCode;
          entity.value = dependentEntity.value;

          activity.preparedDependentEntitiesMap[
            dependentEntity.templateCode
          ] = entity;
        });
    }
  }

  evaluateIsRequired(
    entities: DataEntity[],
    activity: Activity<ActivityModel>
  ): Observable<boolean> {
    if (this.requiredCriteria) {
      DataEntity.prepareDependentEntities(activity);
      return DataEntity.evalCriteria(
        entities,
        activity.preparedDependentEntitiesMap,
        this.requiredCriteria.group
      ).pipe(
        tap(val => {
          this.isRequired = val;
        })
      );
    }

    return of(this.isRequired);
  }

  evaluateIsVisible(
    entities: DataEntity[],
    activity: Activity<ActivityModel>
  ): Observable<boolean> {
    if (this.visibleCriteria) {
      DataEntity.prepareDependentEntities(activity);
      return DataEntity.evalCriteria(
        entities,
        activity.preparedDependentEntitiesMap,
        this.visibleCriteria.group
      ).pipe(
        map(val => {
          this.isVisible = val;
          return val;
        })
      );
    }

    return of(this.isVisible);
  }

  getFormulaValue(): Observable<any> {
    return of(this.value);
  }

  formattedValue: string;
  dataEntityTypeCode: string;
  dataEntityTypeDescription: string;
  dataEntityTypeName: string;
  label?: string;
  origLabel?: string;
  instructions?: string;
  templateCode?: string;
  templateValue?: string;
  isRequired?: boolean;
  isRequiredOriginal?: boolean;
  isRequirement?: boolean;
  isSearchable?: boolean;
  isSensitiveData?: boolean;
  isHelpText: boolean;
  value?: any;
  editorComponentPath?: string;
  editorComponentName?: string;
  editComponentPath?: string;
  editComponentName?: string;
  viewComponentPath?: string;
  viewComponentName?: string;
  parent?: ParentReference;
  parentActivity?: Activity<ActivityModel>;
  parentActivitySurrogate?: Activity<ActivityModel>;
  isSystemEntity?: boolean;
  allowMove?: boolean;
  allowCopy?: boolean;
  allowTemplateCodeEdit?: boolean;
  allowEditDataEntityType?: boolean;
  allowEditSystemEntityRequired?: boolean;
  availablePrintFormatOptions: string[];
  availableConditionalOperators: string[] = [];
  availableFormulaOperators: string[] = [];
  hasCompletedDependencies: boolean;
  isValid = true;
  isLocked: boolean;
  externalDataField: string;
  externalDataSourceId: string;
  availableExternalDataSources?: ExternalDataSource[];
  externalDataSource?: ExternalDataSource;
  canEditExternalValue?: boolean;
  activityId: string;
  activityLabel: string;
  conditionDetails: { activityId: string; activityName: string }[];
  completedDependentActivityIds: string[];
  completedDependentActivtyInfo: ActivityNavigationInfo[];
  canUnlock: boolean;
  isCalculating: boolean;
  isRegistrationCustomField: boolean;
  requiredCriteria: ConditionTarget;
  visibleCriteria: ConditionTarget;
  isVisible: boolean;
  sameActivityFormulaDataEntities: string[];
  isEditMode?: boolean = true;

  hasPreview = true;
  hasEditor = true;

  constructor(options?: Partial<DataEntity>) {
    this.isValid = true;
    if (options) {
      Object.assign(this, options);
    }
  }

  get entityLabel(): string {
    const label = this.label.replace(/<[^>]+>/g, '');

    if (!this.isRequired) {
      return label;
    } else {
      return `${label} <span class="text-red">*<span>`;
    }
  }

  prepareForSave() {
    // override this function to prepare the data entity for saving
    // this is called before the workflow is saved
  }

  getRestrictionMask(): string {
    return '';
  }

  getValidators(): any[] {
    const validators: any[] = [];

    if (this.isRequired) {
      validators.push(Validators.required);
    }

    return validators;
  }

  getAvailableOperators(): ConditionOperator[] {
    return this.availableConditionalOperators.map(o =>
      defaultOperators.find(op => op.operator === o)
    );
  }

  getPossibleValues(): ConditionValue[] {
    return null;
  }

  getPrintTemplateItems(): DataEntityListItem[] {
    return [
      new DataEntityListItem({
        code: this.templateCode,
        text: this.templateCode
      })
    ];
  }

  translateDETextToFormula(text: string): string {
    const regTemplateParser = new RegExp(
      /\<de (?:entity-)?type="(.*)" code="(.*)"\>.*\<\/de\>/,
      'g'
    );

    let match;
    let formattedModel = text;
    while ((match = regTemplateParser.exec(text)) !== null) {
      formattedModel = formattedModel.replace(match[0], `\$\{${match[2]}\}`);
    }
    return formattedModel;
  }

  buildTemplateLabel(fieldInfo: TemplateField) {
    const conditions = fieldInfo.criteria || [];

    if (fieldInfo.fieldType === TemplateFieldType.Image) {
      return conditions.length > 1 ? 'Conditional' : fieldInfo.label;
    }

    const conditionValue =
      conditions.length === 1
        ? this.translateDETextToFormula(conditions[0].value)
        : fieldInfo.label;

    const strippedConditionValue = conditionValue
      .replace('${', '')
      .replace('}', '')
      .toLowerCase();
    const isValueDefault =
      strippedConditionValue === fieldInfo.name.toLowerCase();

    return conditions.length > 1
      ? 'Conditional'
      : isValueDefault
      ? this.translateDETextToFormula(fieldInfo.label)
      : conditionValue;
  }

  updateTemplateItem(fieldInfo: TemplateField, obj: fabric.Object) {
    obj.customData.label = this.buildTemplateLabel(fieldInfo);
  }

  /*
    This returns document dimensions, not screen dimensions
  */
  getTemplateDimensions(
    fieldInfo: TemplateField,
    helperFunctions: any
  ): { width: number; height: number } {
    let width: number = (fieldInfo.width || 0) === 0 ? 50 : fieldInfo.width;
    let height: number = (fieldInfo.height || 0) === 0 ? 20 : fieldInfo.height;

    if (width < 10) {
      width = 10;
    }

    if (height < 10) {
      height = 10;
    }

    return {
      width,
      height
    };
  }

  getFormattedValue(): any {
    return this.value;
  }

  /*
    this is here to add a hook for those entities that need to clear stuff before things are serialized.
  */
  preStringify() {}

  resizeTemplateItem(
    fieldInfo: TemplateField,
    helperFunctions,
    obj: fabric.Object
  ): void {
    if (fieldInfo) {
      obj.left = helperFunctions.documentToScreenX(fieldInfo.x);
      obj.top = helperFunctions.documentToScreenY(fieldInfo.y);
      obj.width = helperFunctions.documentToScreenX(fieldInfo.width);
      obj.height = helperFunctions.documentToScreenY(fieldInfo.height);
      obj.fontSize = helperFunctions.scaleFontSize(fieldInfo.fontSize);

      if (fieldInfo.orientation === FieldOrientation.Vertical) {
        if (fieldInfo.verticalOrientation === VerticalOrientation.Left) {
          obj.angle = 90;
        } else {
          obj.angle = 270;
          obj.left = obj.left - obj.height;
          obj.top = obj.top + obj.width;
        }
      } else {
        obj.angle = 0;
      }

      if (obj.group) {
        // when in a group, coordinates are based on the bottom right of that group, not based on the canvas........
        obj.left = obj.left - obj.group.left - obj.group.width / 2;
        obj.top = obj.top - obj.group.top - obj.group.height / 2;
        // calculate the percentage of the obj relative to the group and size it based on that percentage
        const groupDocWidth = helperFunctions.screenToDocumentX(
          obj.group.width * obj.group.scaleX
        );
        const objWidthPctOfGroup = fieldInfo.width / groupDocWidth;
        obj.width = obj.group.width * objWidthPctOfGroup;
      }

      const smallestDimension = Math.min(obj.height, obj.width);

      // if the font size is bigger than the box, shrink it down to fit
      if (obj.fontSize > smallestDimension) {
        obj.fontSize = smallestDimension;
      }

      obj.setCoords();
    }
  }

  async formatTemplateItem(
    fieldInfo: TemplateField,
    helperFunctions
  ): Promise<fabric.Object> {
    const label = this.buildTemplateLabel(fieldInfo);

    // get document dimensions with minimums applied
    const dimensions = this.getTemplateDimensions(fieldInfo, helperFunctions);

    // document coordinates
    fieldInfo.width = dimensions.width;
    fieldInfo.height = dimensions.height;

    return of(
      new fabric.ResizableTextbox('X', {
        width: helperFunctions.documentToScreenX(fieldInfo.width),
        height: helperFunctions.documentToScreenY(fieldInfo.height),
        left: helperFunctions.documentToScreenX(fieldInfo.x),
        top: helperFunctions.documentToScreenY(fieldInfo.y),
        fontSize: helperFunctions.scaleFontSize(fieldInfo.fontSize),
        stroke: `rgb(${fieldInfo.fontColor || '0, 0, 0'})`,
        editable: false,
        lockScalingY: false,
        lockSkewingY: true,
        lockScalingX: false,
        lockSkewingX: true,
        backgroundColor: this.isValid == false ? 'red' : 'rgba(255, 0, 0, .25)',
        customData: {
          label,
          id: fieldInfo.id,
          code: fieldInfo.name
        }
      })
    ).toPromise();
  }

  getSpecialAvailablePrintFormatOptions(): { [key: string]: string[] } {
    // hook for any data entiy that has specific available print format options
    return null;
  }
}

export class ParentReference {
  id: string;
  name: string;
  type: string;
  activityType?: string;

  constructor(options?: Partial<ParentReference>) {
    if (options) {
      Object.assign(this, options);
    }
  }
}

export class DataEntityOption {
  id: string;
  templateCode: string;
  label: string;
  activityName: string;

  constructor(options?: Partial<DataEntityOption>) {
    if (options) {
      Object.assign(this, options);
    }
  }
}
